import Alert from '@codegouvfr/react-dsfr/Alert';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import type { createModal } from '@codegouvfr/react-dsfr/Modal';
import type { PartialAnalysis } from 'maestro-shared/schema/Analysis/Analysis';
import { FileInput } from 'maestro-shared/schema/File/FileInput';
import type { FileType } from 'maestro-shared/schema/File/FileType';
import { useContext, useEffect, useState } from 'react';
import { assert, type Equals } from 'tsafe';
import { z } from 'zod';
import AppUpload from '../../../../components/_app/AppUpload/AppUpload';
import { useForm } from '../../../../hooks/useForm';
import { ApiClientContext } from '../../../../services/apiClient';

interface Props {
  modal: ReturnType<typeof createModal>;
  sampleId: string;
  itemNumber: number;
  copyNumber: number;
  partialAnalysis: PartialAnalysis | undefined;
}

export const AnalysisDocumentModal = ({
  modal,
  sampleId,
  itemNumber,
  copyNumber,
  partialAnalysis,
  ..._rest
}: Props) => {
  assert<Equals<keyof typeof _rest, never>>();

  const apiClient = useContext(ApiClientContext);

  const [createAnalysis] = apiClient.useCreateAnalysisMutation();
  const [updateAnalysis] = apiClient.useUpdateAnalysisMutation();
  const [
    createAnalysisReportDocument,
    { isLoading: isCreateLoading, isError: isCreateError }
  ] = apiClient.useCreateAnalysisReportDocumentMutation();

  const acceptFileTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png'
  ] as const satisfies FileType[];

  const [fileInput, setFileInput] = useState<File | undefined>();

  const selectFile = async (e: any) => {
    e.preventDefault();
    setFileInput(e?.target?.files?.[0]);
  };

  useEffect(() => {
    if (fileInput) {
      form.validate(async (result) => {
        form.reset();

        let analysisId = partialAnalysis?.id;
        if (!analysisId) {
          const { data: analysis } = await createAnalysis({
            sampleId,
            itemNumber,
            copyNumber
          });
          analysisId = analysis!.id;
        }
        if (analysisId) {
          await createAnalysisReportDocument({
            analysisId,
            file: result.fileInput as File
          });
          await updateAnalysis({
            compliance: partialAnalysis?.compliance ?? null,
            notesOnCompliance: partialAnalysis?.notesOnCompliance ?? null,
            emailReceivedAt: partialAnalysis?.emailReceivedAt,
            residues: partialAnalysis?.residues,
            analysisId,
            status: 'InReview'
          });
        }
        modal.close();
      });
    }
  }, [fileInput]);

  const Form = z.object({
    fileInput: FileInput(acceptFileTypes).nullish()
  });

  const form = useForm(Form, { fileInput });

  return (
    <modal.Component
      title="Rapport d'analyse"
      concealingBackdrop={false}
      topAnchor
      buttons={[
        {
          children: 'Annuler',
          onClick: () => ({}),
          doClosesModal: true,
          priority: 'secondary'
        }
      ]}
    >
      <AppUpload
        label="Ajouter le rapport d'analyse"
        nativeInputProps={{
          onChange: (event: any) => selectFile(event)
        }}
        disabled={isCreateLoading}
        acceptFileTypes={acceptFileTypes}
        inputForm={form}
        inputKey="fileInput"
        whenValid="fichier valide"
        required
      />

      {isCreateError && (
        <Alert
          className={cx('fr-my-2w')}
          description={<>Le dépôt de fichier a échoué.</>}
          severity="error"
          small
        />
      )}
    </modal.Component>
  );
};
